// =============================================================================
// Project Management — org Team page (admin-only)
//
// The org-level stakeholder directory: every ProjectManagementStakeholderProfile (person/company), and a
// person-anchored bulk manager for the selected one — membership across projects, task assignment across
// projects (single-template + full matrix), and the "apply role" accelerator. Distinct from the per-project
// Stakeholders tab, which manages one project's roster.
// =============================================================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { AccountPicker } from '/imports/ui/AccountPicker';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { PartyBulkManager } from '/imports/ui/PartyBulkManager';
import { toastOnError } from '/imports/ui/utils';
import { useUser, useFindOne } from '/imports/hooks';
import { Account, Accounts, accountDisplayName } from '/imports/api/accounts';
import { RichSelect } from '/imports/ui/RichSelect';
import {
  ProjectManagementStakeholderProfile, projectManagementIsAdmin, projectManagementEpcModelClass,
  projectManagementStakeholderProfilesList, projectManagementSaveStakeholderProfile, projectManagementDeleteStakeholderProfile,
} from '/imports/api/projectManagement';

const TeamPageStyled = styled(Page)`
  /* Fill the viewport: the two panes scroll internally so the PAGE never scrolls (no double-scroll). */
  .page-body { min-height: 0; padding-bottom: 1.5rem; }
  .team-layout { display: grid; grid-template-columns: 22rem minmax(0, 1fr); grid-template-rows: minmax(0, 1fr); gap: 1.5rem; align-items: stretch; flex: 1; min-height: 0; }
  ${css.mobileMedia} { .team-layout { grid-template-columns: minmax(0, 1fr); grid-template-rows: none; } }
  .directory { display: flex; flex-direction: column; min-height: 0; border: ${css.inputBorder}; border-radius: ${css.inputBorderRadius}; overflow: hidden; }
  .directory .dir-tools { display: flex; gap: 0.5rem; padding: 0.6rem; border-bottom: 1px solid ${css.grey6}; }
  .directory .dir-tools .search { flex: 1 1 0; min-width: 0; }
  /* Input's inner <input> has no width:100%, so in a shrunk flex item it overflows onto the Add button. */
  .directory .dir-tools .search input { width: 100%; }
  .directory .dir-tools .btn, .directory .dir-tools button { flex: 0 0 auto; }
  .directory .dir-list { flex: 1; min-height: 0; overflow: auto; }
  .directory .dir-row { display: block; width: 100%; text-align: left; background: none; border: none; border-bottom: 1px solid ${css.grey7}; padding: 0.6rem 0.75rem; cursor: pointer; }
  .directory .dir-row:hover { background: ${css.grey7}; }
  .directory .dir-row.active { background: ${css.grey6}; }
  .directory .dir-row .nm { font-weight: 600; color: ${css.grey1}; }
  .directory .dir-row .sub { color: ${css.grey3}; font-size: 0.8rem; }
  .directory .dir-empty { padding: 1.25rem; color: ${css.grey3}; text-align: center; }
  .detail { min-width: 0; display: flex; flex-direction: column; min-height: 0; }
  .detail .detail-head { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .detail .detail-head h2 { margin: 0; }
  .detail .detail-head .detail-actions { display: flex; gap: 0.6rem; margin-left: auto; }
  .detail .detail-sub { color: ${css.grey3}; font-size: 0.9rem; margin: -0.75rem 0 1rem; }
  .detail .placeholder { color: ${css.grey3}; padding: 3rem 1rem; text-align: center; }
  .heading-buttons { display: flex; gap: 0.6rem; }
`;

const ProfileModalStyled = styled(ModalStyled)`
  .modal-window { width: 34rem; max-width: 100%; }
  .fields { display: flex; flex-direction: column; gap: 0.75rem; }
  /* Input renders an inline span around an intrinsic-width <input>; make both fill so every field spans the modal. */
  .fields .input { display: block; }
  .fields .input input { width: 100%; }
  .meta-row { color: ${css.grey3}; font-size: 0.9rem; margin-bottom: 0.15rem; }
  .modal-buttons { display: flex; gap: 0.75rem; justify-content: space-between; margin-top: 1.25rem; flex-wrap: wrap; }
`;

/** Create/edit one stakeholder profile (org identity). */
const ProfileModal = (props: { profile?: ProjectManagementStakeholderProfile; onClose: (savedId?: string) => void }) => {
  const { profile, onClose } = props;
  const [accountId, setAccountId] = useState<string | undefined>(profile?.accountId);
  const [name, setName] = useState(profile?.name || '');
  const [kind, setKind] = useState<'internal' | 'external'>(profile?.kind || 'external');
  const organization = profile?.organization || '';  // field hidden; value preserved on save
  const [email, setEmail] = useState(profile?.email || '');
  // The EPC this person works for. What a lifecycle's 'partner' tasks resolve through, so pointing a
  // profile at a party hands them every unassigned partner task on the projects they belong to.
  const [partyId, setPartyId] = useState<string | undefined>(profile?.partyId);

  const pickAccount = (id?: string, account?: Account) => {
    setAccountId(id);
    if (account) { setName(accountDisplayName(account)); setKind('internal'); if (account.email) setEmail(account.email); }
  };
  const save = toastOnError(async () => {
    if (!name.trim()) { toast.info(strings.Name()); return; }
    const id = await projectManagementSaveStakeholderProfile.call({
      _id: profile?._id, name: name.trim(), kind, organization: organization || undefined,
      email: email || undefined, accountId, partyId,
    });
    onClose(id);
  });

  return (
    <Modal styled={ProfileModalStyled} title={profile ? strings.Edit() : strings.Add()} onClose={() => onClose()}>
      <div className="fields">
        <div><div className="meta-row">{strings.Account()}</div><AccountPicker value={accountId} onChange={pickAccount} /></div>
        <div><div className="meta-row">{strings.Name()}</div><Input value={name} onChange={(v) => setName(v || '')} /></div>
        <div><div className="meta-row">{strings.Email()}</div><Input value={email} onChange={(v) => setEmail(v || '')} /></div>
        <div>
          <div className="meta-row">{strings.Epc()}</div>
          <RichSelect
            listMethod={projectManagementEpcModelClass.listMethod}
            value={partyId}
            preSelect={profile?.partyId ? { modelClass: projectManagementEpcModelClass, document: profile, field: 'partyId' as any } : undefined}
            clearable
            onChange={(option) => setPartyId(option.value)}
            onClear={() => setPartyId(undefined)}
          />
        </div>
      </div>
      <div className="modal-buttons">
        <Button className="secondary" onClick={() => onClose()}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};

/** Org stakeholder directory + person-anchored bulk manager. Admin-only. */
export const ProjectManagementTeamPage = () => {
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  const [profiles, setProfiles] = useState<ProjectManagementStakeholderProfile[]>();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string>();
  const [editing, setEditing] = useState<{ profile?: ProjectManagementStakeholderProfile } | undefined>();
  const [confirmDelete, setConfirmDelete] = useState<ProjectManagementStakeholderProfile>();
  const [bulkKey, setBulkKey] = useState(0);

  const load = async (q?: string) => {
    const res = await projectManagementStakeholderProfilesList.call({ search: q });
    setProfiles(res);
    return res;
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 250); return () => clearTimeout(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [search]);

  const isAdmin = projectManagementIsAdmin(user, account);
  if (user && !isAdmin) return <NotPermittedPage />;

  const selected = profiles?.find((p) => p._id === selectedId);

  const onSaved = async (savedId?: string) => {
    setEditing(undefined);
    const res = await load(search);
    if (savedId) {
      setSelectedId(savedId);
      if (res.find((p) => p._id === savedId)) setBulkKey((k) => k + 1);  // refresh the manager for the edited profile
    }
  };
  const doDeleteProfile = (profile: ProjectManagementStakeholderProfile) => toastOnError(async () => {
    await projectManagementDeleteStakeholderProfile.call({ id: profile._id });
    toast.success(strings.Deleted());
    if (selectedId === profile._id) setSelectedId(undefined);
    setConfirmDelete(undefined);
    await load(search);
  })();

  return (
    <TeamPageStyled>
      <PageHeading
        left={<h1>{strings.Team()}</h1>}
        right={<div className="heading-buttons"><Link to="/project-management"><Button className="secondary">{strings.Pipeline()}</Button></Link></div>}
      />
      <PageBody>
        <div className="team-layout">
          <div className="directory">
            <div className="dir-tools">
              <Input className="search" value={search} onChange={(v) => setSearch(v || '')} icon="search" placeholder={strings.Search()} />
              <Button className="secondary" onClick={() => setEditing({})}>{strings.Add()}</Button>
            </div>
            <div className="dir-list">
              {
                !profiles ? <LoadingFlex />
                  : !profiles.length ? <div className="dir-empty">{strings.NoResults()}</div>
                    : profiles.map((p) => (
                      <button key={p._id} className={`dir-row${p._id === selectedId ? ' active' : ''}`} onClick={() => setSelectedId(p._id)}>
                        <div className="nm">{p.name}</div>
                        {(p.organization || p.email) && <div className="sub">{[p.organization, p.email].filter(Boolean).join(' · ')}</div>}
                      </button>
                    ))
              }
            </div>
          </div>

          <div className="detail">
            {
              !selected
                ? <div className="placeholder">{strings.SelectStakeholderHint()}</div>
                : <>
                  <div className="detail-head">
                    <h2>{selected.name}</h2>
                    <div className="detail-actions">
                      <Button className="secondary" onClick={() => setEditing({ profile: selected })}>{strings.Edit()}</Button>
                      <Button className="secondary" onClick={() => setConfirmDelete(selected)}>{strings.Delete()}</Button>
                    </div>
                  </div>
                  {(selected.organization || selected.email) && <div className="detail-sub">{[selected.organization, selected.email].filter(Boolean).join(' · ')}</div>}
                  <PartyBulkManager key={`${selected._id}-${bulkKey}`} profileId={selected._id} mode="full" />
                </>
            }
          </div>
        </div>
      </PageBody>
      {editing && <ProfileModal profile={editing.profile} onClose={onSaved} />}
      {
        confirmDelete &&
        <ConfirmModal
          title={`${strings.Delete()} "${confirmDelete.name}"`}
          body={strings.DeleteProfileConfirm(confirmDelete.name)}
          onConfirm={() => doDeleteProfile(confirmDelete)}
          onClose={() => setConfirmDelete(undefined)}
        />
      }
    </TeamPageStyled>
  );
};
